import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PremiumSection from "../../home/components/PremiumSection";
import EyeIcon from "../../../components/icons/EyeIcon";
import { useBlogList } from "../hooks/useBlogList";
import { normalizeLanguage } from "../../../utils/language";
import { createSlug } from "../../../utils/slug";
import type { BlogListItem } from "../types";

const createBlogUrl = (blog: BlogListItem, language: "tr" | "en") => {
  const slug = createSlug(blog.baslik, language);
  return `/blog-yazilari/${slug}`;
};

const BlogListPage = () => {
  const { i18n, t } = useTranslation();
  const language = normalizeLanguage(i18n.language);
  const { data, isLoading, isError } = useBlogList(language);

  const blogs = data?.blog ?? [];

  const topBlogs = useMemo(() => {
    return [...blogs].sort((a, b) => b.okunma_sayisi - a.okunma_sayisi).slice(0, 5);
  }, [blogs]);

  const suggestedBlogs = useMemo(() => {
    return [...blogs].sort(() => Math.random() - 0.5).slice(0, 6);
  }, [blogs]);

  if (isError) {
    return (
      <div className="text-red-500 text-center pt-32 text-xl">
        {t("blogPage.errorLoading")}
      </div>
    );
  }

  return (
    <div>
      <div className="pt-28 text-white w-[90%] sm:w-[85%] mx-auto">
        <div className="blogPage-title h-32 flex justify-center items-center">
          <h1 className="text-4xl">{t("blogPage.mostRead")}</h1>
        </div>

        <div className="blogPage-content gap-1 sm:gap-3 grid grid-cols-1 sm:grid-cols-4 p-4 sm:p-8 bg-customPurple-light/40 shadow-custom-4-sided rounded-3xl">
          {isLoading ? (
            <div className="col-span-4 flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-white" />
            </div>
          ) : topBlogs.length > 0 ? (
            topBlogs.map((topBlog) => (
              <Link
                to={createBlogUrl(topBlog, language)}
                key={topBlog.blog_id}
                className="blog-box-top min-h-52 my-1 border border-customPurple-premium relative bg-cover bg-center rounded-xl transition-all hover:shadow-custom-rounded-hover2"
                style={{
                  backgroundImage: `url(https://panel.therapoloji.freelancer.ist/uploads/${topBlog.img_url})`,
                }}
              >
                <div className="absolute bottom-0 rounded-b-xl bg-gradient-to-b from-transparent to-customPurple w-full p-4">
                  <div className="w-16 grid grid-cols-2 bg-black/10 p-2 rounded-2xl mb-2">
                    <EyeIcon />
                    <span className="text-[13px]">{topBlog.okunma_sayisi}</span>
                  </div>
                  <span className="text-xl">{topBlog.baslik}</span>
                </div>
              </Link>
            ))
          ) : (
            <div className="col-span-4 text-center py-10">
              {t("blogPage.noPosts")}
            </div>
          )}
        </div>

        <div className="mt-32">
          <h2 className="text-4xl text-center mb-8">{t("blogPage.allPosts")}</h2>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-[70%]">
              {isLoading ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-white mx-auto" />
                </div>
              ) : (
                blogs.map((blog) => (
                  <Link
                    to={createBlogUrl(blog, language)}
                    key={blog.blog_id}
                    className="block mb-4 bg-customPurple-light/50 rounded-xl shadow-custom-4-sided overflow-hidden hover:shadow-custom-rounded-hover2"
                  >
                    <div className="flex flex-col sm:flex-row">
                      <img
                        src={`https://panel.therapoloji.freelancer.ist/uploads/${blog.img_url}`}
                        alt={blog.baslik}
                        className="w-full sm:w-96 h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="p-4 flex-1">
                        <span className="text-customPurple text-sm">
                          {blog.blog_kategori}
                        </span>
                        <h3 className="text-xl my-2 line-clamp-2">{blog.baslik}</h3>
                        <p className="text-sm line-clamp-3">{blog.aciklama}</p>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>

            <div className="w-full lg:w-[30%]">
              <div className="bg-customPurple-light/50 rounded-xl p-6 shadow-custom-4-sided">
                <h3 className="text-2xl mb-6 pb-4 border-b border-customPurple/30">
                  {t("blogPage.suggested")}
                </h3>
                <div className="flex flex-col gap-4">
                  {suggestedBlogs.map((blog) => (
                    <Link key={blog.blog_id} to={createBlogUrl(blog, language)} className="group">
                      <span className="text-sm text-customPurple">
                        {blog.blog_kategori}
                      </span>
                      <p className="group-hover:text-customPurple transition line-clamp-2">
                        {blog.baslik}
                      </p>
                      <div className="h-px bg-customPurple/30 mt-3" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PremiumSection className="bg-gradient-to-b from-transparent to-blog mt-20" />
    </div>
  );
};

export default BlogListPage;
